import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { uploadOptimizedImage, reoptimizeStoredImage } from "@/lib/image-upload";
import {
  getCulinaryPageData,
  upsertCulinaryContent,
  addMenuImage,
  deleteMenuImage,
  reorderMenuImages,
  addGalleryImage,
  deleteGalleryImage,
  reorderGalleryImages,
  updateMenuImageUrl,
  updateGalleryImageUrl,
  type MenuType,
  type MenuImage,
  type GalleryImage,
  type CulinaryContent,
} from "@/lib/culinary.functions";
import { FieldOverridesButton } from "@/components/admin/overrides/FieldOverridesButton";
import { ImageExperiencesCell } from "@/components/admin/overrides/ImageExperiencesCell";
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Trash2,
  Upload,
  Loader2,
} from "lucide-react";

const PAGE_KEY = "culinary";
const uploadHero = async (f: File) => {
  const { url } = await uploadOptimizedImage({
    file: f,
    bucket: "slide-media",
    folder: "culinary/hero",
  });
  return url;
};

export const Route = createFileRoute("/_authenticated/admin/culinary")({
  head: () => ({
    meta: [{ title: "Culinary CMS" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminCulinaryPage,
});

async function uploadToSlideMedia(file: File, folder: string): Promise<string> {
  const { url } = await uploadOptimizedImage({
    file,
    bucket: "slide-media",
    folder: `culinary/${folder}`,
  });
  return url;
}


function AdminCulinaryPage() {
  const fetch = useServerFn(getCulinaryPageData);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "culinary"],
    queryFn: () => fetch(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "culinary"] });
    qc.invalidateQueries({ queryKey: ["culinary", "page"] });
  };

  if (isLoading || !data) {
    return (
      <div className="min-h-screen bg-background p-10 text-foreground/60">
        Loading…
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link
              to="/admin"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-3 h-3" /> Admin
            </Link>
            <h1 className="text-2xl font-serif">Culinary CMS</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Edit the culinary page hero, dining inclusions, menu images, and gallery.
            </p>
          </div>
          <OptimizeAllButton
            content={data.content}
            menus={data.menus}
            gallery={data.gallery}
            onDone={invalidate}
          />
        </div>


        <ContentPanel
          content={data.content}
          onSaved={invalidate}
        />

        {(["breakfast", "lunch", "dinner"] as MenuType[]).map((type) => (
          <MenuPanel
            key={type}
            type={type}
            images={data.menus.filter((m) => m.menu_type === type)}
            onChanged={invalidate}
          />
        ))}

        <GalleryPanel images={data.gallery} onChanged={invalidate} />
      </div>
    </div>
  );
}

function ContentPanel({
  content,
  onSaved,
}: {
  content: CulinaryContent | null;
  onSaved: () => void;
}) {
  const upsert = useServerFn(upsertCulinaryContent);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const [heroUrl, setHeroUrl] = useState(content?.hero_image_url ?? "");
  const [kicker, setKicker] = useState(content?.hero_kicker ?? "");
  const [title, setTitle] = useState(content?.hero_title ?? "");
  const [subtitle, setSubtitle] = useState(content?.hero_subtitle ?? "");
  const [inclusions, setInclusions] = useState(content?.inclusions_sentence ?? "");

  const save = useMutation({
    mutationFn: () =>
      upsert({
        data: {
          id: content?.id,
          hero_image_url: heroUrl || null,
          hero_kicker: kicker || null,
          hero_title: title || null,
          hero_subtitle: subtitle || null,
          inclusions_sentence: inclusions || null,
        },
      }),
    onSuccess: () => {
      toast.success("Saved");
      onSaved();
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const handleUpload = async (f: File) => {
    setUploading(true);
    try {
      const url = await uploadToSlideMedia(f, "hero");
      setHeroUrl(url);
      // Auto-persist the new hero image so it isn't lost if the user
      // navigates away before clicking Save.
      await upsert({
        data: {
          id: content?.id,
          hero_image_url: url,
          hero_kicker: kicker || null,
          hero_title: title || null,
          hero_subtitle: subtitle || null,
          inclusions_sentence: inclusions || null,
        },
      });
      toast.success("Hero image saved");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-5">
      <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
        Hero & intro
      </h2>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Hero image</Label>
          <FieldOverridesButton
            pageKey={PAGE_KEY}
            fieldKey="hero_image"
            label="Hero image"
            type="image"
            onUploadImage={uploadHero}
          />
        </div>
        <div className="flex items-start gap-3">
          {heroUrl ? (
            <img
              src={heroUrl}
              alt=""
              className="w-32 h-32 object-cover rounded"
            />
          ) : (
            <div className="w-32 h-32 rounded bg-muted" />
          )}
          <div className="flex-1 space-y-2">
            <Input
              value={heroUrl}
              onChange={(e) => setHeroUrl(e.target.value)}
              placeholder="https://… or upload"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1" />
              )}
              Upload
            </Button>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Kicker</Label>
          <FieldOverridesButton pageKey={PAGE_KEY} fieldKey="hero_kicker" label="Kicker" type="text" />
        </div>
        <Input value={kicker} onChange={(e) => setKicker(e.target.value)} placeholder="The Table" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Title</Label>
          <FieldOverridesButton pageKey={PAGE_KEY} fieldKey="hero_title" label="Title" type="text" />
        </div>
        <Textarea value={title} onChange={(e) => setTitle(e.target.value)} rows={2} placeholder={"Culinary\n(press Enter to break line)"} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Subtitle</Label>
          <FieldOverridesButton pageKey={PAGE_KEY} fieldKey="hero_subtitle" label="Subtitle" type="longtext" />
        </div>
        <Textarea
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          rows={2}
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Dining inclusions sentence</Label>
          <FieldOverridesButton pageKey={PAGE_KEY} fieldKey="inclusions_sentence" label="Dining inclusions" type="longtext" />
        </div>
        <Textarea
          value={inclusions}
          onChange={(e) => setInclusions(e.target.value)}
          rows={3}
          placeholder="Use a line break to separate sentences."
        />
        <p className="text-xs text-muted-foreground mt-1">
          Line breaks are preserved on the page.
        </p>
      </div>

      <Button onClick={() => save.mutate()} disabled={save.isPending}>
        {save.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Save
      </Button>
    </div>
  );
}

function MenuPanel({
  type,
  images,
  onChanged,
}: {
  type: MenuType;
  images: MenuImage[];
  onChanged: () => void;
}) {
  const add = useServerFn(addMenuImage);
  const del = useServerFn(deleteMenuImage);
  const reorder = useServerFn(reorderMenuImages);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files: FileList) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const url = await uploadToSlideMedia(f, `menus/${type}`);
        await add({ data: { menu_type: type, image_url: url } });
      }
      toast.success(`Added ${files.length} image${files.length === 1 ? "" : "s"}`);
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...images];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    await reorder({
      data: { menu_type: type, ordered_ids: next.map((i) => i.id) },
    });
    onChanged();
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          {type} menu
        </h2>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) handleFiles(e.target.files);
            }}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-1" />
            )}
            Upload images
          </Button>
        </div>
      </div>

      {images.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No menu images yet. Upload one or more images of the {type} menu.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map((img, i) => (
            <div
              key={img.id}
              className="relative group rounded-md overflow-hidden border border-border bg-background"
            >
              <img
                src={img.image_url}
                alt=""
                className="w-full aspect-[3/4] object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/60 p-1">
                <div className="flex">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-white hover:text-white"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-white hover:text-white"
                    disabled={i === images.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-white hover:text-white"
                  onClick={async () => {
                    await del({ data: { id: img.id } });
                    onChanged();
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryPanel({
  images,
  onChanged,
}: {
  images: GalleryImage[];
  onChanged: () => void;
}) {
  const add = useServerFn(addGalleryImage);
  const del = useServerFn(deleteGalleryImage);
  const reorder = useServerFn(reorderGalleryImages);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files: FileList) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const url = await uploadToSlideMedia(f, "gallery");
        await add({ data: { image_url: url } });
      }
      toast.success(`Added ${files.length} image${files.length === 1 ? "" : "s"}`);
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...images];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    await reorder({ data: { ordered_ids: next.map((i) => i.id) } });
    onChanged();
  };

  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Gallery
        </h2>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) handleFiles(e.target.files);
            }}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-1" />
            )}
            Upload images
          </Button>
        </div>
      </div>

      {images.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No gallery images yet. Upload one or more.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map((img, i) => (
            <div
              key={img.id}
              className="relative group rounded-md overflow-hidden border border-border bg-background"
            >
              <img
                src={img.image_url}
                alt=""
                className="w-full aspect-square object-cover"
              />
              <ImageExperiencesCell imageTable="culinary_gallery_images" imageId={img.id} />
              <div className="flex items-center justify-between gap-1 bg-black/60 p-1">
                <div className="flex">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-white hover:text-white"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-white hover:text-white"
                    disabled={i === images.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-white hover:text-white"
                  onClick={async () => {
                    await del({ data: { id: img.id } });
                    onChanged();
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OptimizeAllButton({
  content,
  menus,
  gallery,
  onDone,
}: {
  content: CulinaryContent | null;
  menus: MenuImage[];
  gallery: GalleryImage[];
  onDone: () => void;
}) {
  const upsertContent = useServerFn(upsertCulinaryContent);
  const updateMenu = useServerFn(updateMenuImageUrl);
  const updateGallery = useServerFn(updateGalleryImageUrl);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null,
  );

  // Count items eligible for re-optimization (anything not already .webp/.svg/.gif).
  const isOptimized = (u?: string | null) => {
    if (!u) return true;
    const c = u.split("?")[0].toLowerCase();
    return c.endsWith(".webp") || c.endsWith(".svg") || c.endsWith(".gif");
  };
  const heroNeeds = !isOptimized(content?.hero_image_url);
  const menuNeeds = menus.filter((m) => !isOptimized(m.image_url));
  const galleryNeeds = gallery.filter((g) => !isOptimized(g.image_url));
  const totalNeeds = (heroNeeds ? 1 : 0) + menuNeeds.length + galleryNeeds.length;

  const run = async () => {
    if (totalNeeds === 0) {
      toast.success("All images are already optimized");
      return;
    }
    setRunning(true);
    setProgress({ done: 0, total: totalNeeds });
    let done = 0;
    let failed = 0;
    const tick = () => {
      done += 1;
      setProgress({ done, total: totalNeeds });
    };

    try {
      if (heroNeeds && content) {
        try {
          const res = await reoptimizeStoredImage({
            url: content.hero_image_url!,
            bucket: "slide-media",
            folder: "culinary/hero",
          });
          if (res) {
            await upsertContent({
              data: {
                id: content.id,
                hero_image_url: res.url,
                hero_kicker: content.hero_kicker,
                hero_title: content.hero_title,
                hero_subtitle: content.hero_subtitle,
                inclusions_sentence: content.inclusions_sentence,
              },
            });
          }
        } catch (e) {
          console.warn("Hero re-optimize failed", e);
          failed++;
        }
        tick();
      }

      for (const m of menuNeeds) {
        try {
          const res = await reoptimizeStoredImage({
            url: m.image_url,
            bucket: "slide-media",
            folder: `culinary/menus/${m.menu_type}`,
          });
          if (res) await updateMenu({ data: { id: m.id, image_url: res.url } });
        } catch (e) {
          console.warn("Menu image re-optimize failed", e);
          failed++;
        }
        tick();
      }

      for (const g of galleryNeeds) {
        try {
          const res = await reoptimizeStoredImage({
            url: g.image_url,
            bucket: "slide-media",
            folder: "culinary/gallery",
          });
          if (res) await updateGallery({ data: { id: g.id, image_url: res.url } });
        } catch (e) {
          console.warn("Gallery image re-optimize failed", e);
          failed++;
        }
        tick();
      }

      if (failed > 0) {
        toast.warning(
          `Optimized ${totalNeeds - failed} of ${totalNeeds} images (${failed} failed)`,
        );
      } else {
        toast.success(`Optimized ${totalNeeds} image${totalNeeds === 1 ? "" : "s"}`);
      }
      onDone();
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={run}
      disabled={running || totalNeeds === 0}
      title={
        totalNeeds === 0
          ? "All images are already optimized"
          : `${totalNeeds} image${totalNeeds === 1 ? "" : "s"} can be optimized`
      }
    >
      {running ? (
        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
      ) : (
        <Upload className="w-4 h-4 mr-1" />
      )}
      {running && progress
        ? `Optimizing ${progress.done}/${progress.total}`
        : totalNeeds === 0
          ? "Images optimized"
          : `Optimize ${totalNeeds} image${totalNeeds === 1 ? "" : "s"}`}
    </Button>
  );
}

